import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService } from './auth.service';

/**
 * Versão da conta em uso. O nome antigo do conceito ("conta global") é resíduo
 * da era Twilio; aqui a operação é nacional ou internacional.
 */
export type AccountVersion = 'national' | 'international';

export const NATIONAL: AccountVersion = 'national';
export const INTERNATIONAL: AccountVersion = 'international';

/**
 * Versão corrente como stream — a FONTE ÚNICA do realm para o painel.
 *
 * Existe porque `AuthService.toggleGlobalAccount()` só emite `userChanged`:
 * tela já aberta não refaz fetch e, com duas moedas em jogo, continuaria
 * mostrando o valor da versão anterior. A persistência segue sendo do
 * AuthService (`AuthService.isGlobalAccount`, hoje derivado do `globalActivated`
 * do usuário efetivo) — este serviço só a observa e a publica.
 *
 * ## Quem quer o realm assina daqui
 *
 * Nenhuma tela lê `localStorage` nem `user.globalActivated` para decidir o que
 * buscar. Assinar `getVersion()` dá as duas coisas de uma vez: a primeira
 * emissão do `BehaviorSubject` é a carga inicial e cada troca é o gatilho do
 * fetch novo — daí o `switchMap` nas telas, que cancela a requisição em voo do
 * realm anterior.
 *
 * ## A ordem é do AuthService
 *
 * Este serviço emite DEPOIS que o flag já está gravado porque só reage a
 * `userChanged`, que o `AuthService` emite depois de persistir (ver
 * `AuthService.toggleGlobalAccount` e `setUser`). É isso que faz a requisição
 * disparada pela emissão falar do realm NOVO: o `UserInterceptor` monta
 * `&global=` lendo o usuário gravado no momento em que a request sai.
 */
@Injectable({ providedIn: 'root' })
export class AccountVersionService {

  private versionSubject: BehaviorSubject<AccountVersion>;

  constructor(private authService: AuthService) {
    this.versionSubject = new BehaviorSubject<AccountVersion>(this.readVersion());

    // `toggleGlobalAccount()` termina em `userChanged`; quem escuta aqui não
    // precisa saber de onde a versão sai.
    this.authService.userChanged.subscribe(() => this.refresh());
    // Logout e login na mesma aba não recarregam a página: sem isto o singleton ficava no
    // realm da sessão anterior (pílula, aba da carteira e tema de uma, `&global=` de outra).
    this.authService.sessionChanged.subscribe(() => this.refresh());
  }

  /**
   * Sem sessão vale o realm persistido (nacional depois do logout), mesmo que o stream ainda não
   * o tenha publicado — ver `refresh()`. É o que as telas públicas (login) leem pelo `themeClass`.
   */
  get version(): AccountVersion {
    return this.authService.isLogged() ? this.versionSubject.value : this.readVersion();
  }

  get isInternational(): boolean {
    return this.version === INTERNATIONAL;
  }

  /** Classe de tema aplicada no shell. Ver `src/theme.scss`. */
  get themeClass(): string {
    return `version-${this.version}`;
  }

  getVersion(): Observable<AccountVersion> {
    return this.versionSubject.asObservable();
  }

  /**
   * Troca a versão.
   *
   * Não republica aqui de propósito: quem emite é o `refresh()` pendurado em
   * `userChanged`, que o `AuthService` dispara depois de gravar. Um `refresh()`
   * extra nesta linha esconderia uma inversão de ordem lá dentro — a emissão
   * sairia daqui, já com o flag gravado, e o teste passaria mesmo com a
   * reatividade quebrada para quem trocasse o realm por outro caminho.
   */
  toggle(): void {
    this.authService.toggleGlobalAccount();
  }

  /**
   * Relê a versão persistida e emite se mudou.
   *
   * No fim da sessão (`removeData`: logout, refresh que falhou, token recusado) NÃO publica: quem
   * assina o stream são as telas ainda abertas, que recarregariam o realm novo sem token antes da
   * navegação para o login (Minha Carteira, modal do header…). O `version`/`themeClass` já dizem o
   * realm sem sessão, e a próxima sessão (`saveData`) publica o dela.
   */
  refresh(): void {
    const version = this.readVersion();

    if (version === this.versionSubject.value || !this.authService.isLogged()) {
      return;
    }

    this.versionSubject.next(version);
  }

  private readVersion(): AccountVersion {
    return this.authService.isGlobalAccount ? INTERNATIONAL : NATIONAL;
  }
}
